import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { Alert } from '../../components/ui/Alert';
import { Avatar } from '../../components/ui/Avatar';
import { Button } from '../../components/ui/Button';
import { Card, Chip } from '../../components/ui/Card';
import { SkeletonRow } from '../../components/ui/Loader';
import { ApiError, api } from '../../lib/api';
import { countryName, zoneLabel, type ApplicationStatus } from '../hosting/api';
import { Textarea } from '../../components/ui/form';

interface ReviewApplication {
  id: number;
  status: ApplicationStatus;
  country: string;
  timezone: string;
  pitch: string;
  experience: string | null;
  genres: { id: number; name: string; slug: string }[];
  rights_ack: boolean;
  created_at: string;
  decided_at: string | null;
  decision_note: string | null;
  reviewer: string | null;
  applicant: {
    id: number;
    username: string;
    display_name: string;
    status: string;
    created_at: string;
    avatar: string | null;
    follower_count: number;
    track_count: number;
  };
}

const TABS: { id: ApplicationStatus; label: string }[] = [
  { id: 'pending', label: 'Waiting' },
  { id: 'approved', label: 'Approved' },
  { id: 'declined', label: 'Declined' },
  { id: 'withdrawn', label: 'Withdrawn' },
];

/**
 * The host application queue (HANDOFF §6.8).
 *
 * ## Oldest first, and no way to re-sort
 *
 * The server orders it and this page does not offer an alternative. A queue
 * read newest-first starves its own bottom, and the application nobody ever got
 * to is reliably somebody who gave up on the site.
 *
 * ## Everything needed for a decision is on the row
 *
 * Account age, followers, released tracks, the pitch in full. A reviewer who
 * has to open another tab to check whether an applicant has ever uploaded
 * anything will stop checking by the fortieth row, and the queue quietly
 * becomes a rubber stamp.
 */
export function AdminHostApplicationsPage() {
  const [tab, setTab] = useState<ApplicationStatus>('pending');

  const { data, isPending } = useQuery({
    queryKey: ['admin', 'host-applications', tab],
    queryFn: () =>
      api.get<{
        status: ApplicationStatus;
        total: number;
        pending_count: number;
        items: ReviewApplication[];
      }>(`/admin/host-applications?status=${tab}`),
    placeholderData: (previous) => previous,
  });

  const items = data?.items ?? [];

  return (
    <div>
      <h1 className="text-2xl font-semibold tracking-tight text-ink">Host applications</h1>
      <p className="mt-1.5 text-sm text-soft">
        Answered oldest first. Approving grants the host role; declining keeps the application on
        record and lets them apply again.
      </p>

      <div className="mt-6">
      </div>

      <div role="tablist" className="mt-6 flex flex-wrap gap-1">
        {TABS.map((option) => (
          <button
            key={option.id}
            role="tab"
            type="button"
            aria-selected={tab === option.id}
            data-application-tab={option.id}
            onClick={() => setTab(option.id)}
            className={`flex items-center gap-2 rounded-[var(--radius-pill)] px-4 py-2 text-sm transition-colors ${
              tab === option.id ? 'bg-raised font-medium text-ink' : 'text-soft hover:text-ink'
            }`}
          >
            {option.label}
            {/* Only on the tab it is about, and only when it is not zero: a
                badge reading 0 is a badge nobody stops noticing. */}
            {option.id === 'pending' && (data?.pending_count ?? 0) > 0 && (
              <span className="rounded-[var(--radius-pill)] bg-accent px-2 py-0.5 font-mono text-[10px] text-accent-ink">
                {data?.pending_count}
              </span>
            )}
          </button>
        ))}
      </div>

      {isPending ? (
        <div className="mt-6 flex flex-col gap-2" aria-busy="true">
          {[0, 1, 2].map((i) => (
            <SkeletonRow key={i} />
          ))}
        </div>
      ) : items.length === 0 ? (
        <Card className="mt-6 p-6">
          <p className="text-sm text-soft">
            {tab === 'pending' ? 'Nothing waiting. ' : 'Nothing here. '}
            {tab === 'pending' && 'The queue is clear.'}
          </p>
        </Card>
      ) : (
        <ul className="mt-6 flex flex-col gap-4">
          {items.map((application) => (
            // On the `li`, not on the Card inside it: Card renders only the
            // props it declares and drops the rest.
            <li key={application.id} data-application={application.id}>
              <ApplicationCard application={application} />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function ApplicationCard({ application }: { application: ReviewApplication }) {
  const queryClient = useQueryClient();
  const [note, setNote] = useState('');
  const [open, setOpen] = useState<'approve' | 'decline' | null>(null);

  const decide = useMutation({
    mutationFn: (decision: 'approve' | 'decline') =>
      api.post(`/admin/host-applications/${application.id}/decision`, {
        decision,
        note: note.trim() || null,
      }),
    onSuccess: () => {
      // The whole queue, not this row: an approval moves it to another tab and
      // changes the pending count on all of them.
      void queryClient.invalidateQueries({ queryKey: ['admin', 'host-applications'] });
    },
  });

  const pending = application.status === 'pending';
  const { applicant } = application;

  return (
    <Card className="flex flex-col gap-5 p-5">
      {/* ----------------------------------------------------------------- */}
      {/* Who                                                                */}
      {/* ----------------------------------------------------------------- */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <Link to={`/${applicant.username}`} className="group flex items-center gap-3">
          <Avatar src={applicant.avatar} name={applicant.display_name} size={44} />
          <span className="flex flex-col">
            <span className="font-medium text-ink group-hover:underline">
              {applicant.display_name}
            </span>
            <span className="font-mono text-xs text-faint">@{applicant.username}</span>
          </span>
        </Link>

        <dl className="flex flex-wrap gap-x-6 gap-y-1 text-right font-mono text-xs">
          <Stat label="Joined" value={shortDate(applicant.created_at)} />
          <Stat label="Tracks" value={String(applicant.track_count)} />
          <Stat label="Followers" value={applicant.follower_count.toLocaleString()} />
        </dl>
      </div>

      {/* An applicant who has been suspended since applying is the one case a
          reviewer must not miss, so it is called out rather than left to be
          noticed on their profile. */}
      {applicant.status !== 'active' && (
        <Alert tone="error">This account is {applicant.status}.</Alert>
      )}

      {/* ----------------------------------------------------------------- */}
      {/* What                                                               */}
      {/* ----------------------------------------------------------------- */}
      <div className="flex flex-wrap items-center gap-2">
        <Chip>{countryName(application.country)}</Chip>
        <Chip>{zoneLabel(application.timezone)}</Chip>
        {application.genres.map((genre) => (
          <Chip key={genre.id} tone="signal">
            {genre.name}
          </Chip>
        ))}
        {/* Cannot currently be false - the endpoint refuses without it - but
            shown so a reviewer never has to assume. */}
        {!application.rights_ack && <Chip tone="hot">No rights agreement</Chip>}
      </div>

      <p className="whitespace-pre-wrap text-sm leading-relaxed text-soft">{application.pitch}</p>

      {application.experience && (
        <div className="flex flex-col gap-1">
          <span className="meta-caps text-faint">Experience</span>
          <p className="break-words text-sm text-soft">{application.experience}</p>
        </div>
      )}

      {/* ----------------------------------------------------------------- */}
      {/* Decide                                                             */}
      {/* ----------------------------------------------------------------- */}
      {pending ? (
        <div className="flex flex-col gap-3 border-t border-line pt-4">
          {open && (
            <div className="flex flex-col gap-1.5">
              <label htmlFor={`note-${application.id}`} className="text-sm font-medium text-soft">
                {open === 'decline' ? 'Why not, in a sentence' : 'Anything to say?'}
              </label>
              <Textarea
                id={`note-${application.id}`}
                value={note}
                maxLength={500}
                autoFocus
                data-decision-note
                onChange={(event) => setNote(event.target.value)}
                className="min-h-20 resize-y rounded-[var(--radius-control)] border border-line bg-app px-3 py-2.5 text-sm text-ink placeholder:text-faint focus:border-line-strong"
                placeholder={
                  open === 'decline'
                    ? 'They will see this. "Not yet, and here is why" is the only decline worth sending.'
                    : 'They will see this. Optional.'
                }
              />
            </div>
          )}

          {decide.isError && (
            <Alert tone="error">
              {decide.error instanceof ApiError
                ? decide.error.message
                : 'That could not be recorded.'}
            </Alert>
          )}

          <div className="flex flex-wrap items-center gap-2">
            {open === null ? (
              <>
                <Button onClick={() => setOpen('approve')} data-open-approve>
                  Approve
                </Button>
                <Button variant="ghost" onClick={() => setOpen('decline')} data-open-decline>
                  Decline
                </Button>
              </>
            ) : (
              <>
                <Button
                  variant={open === 'decline' ? 'danger' : 'primary'}
                  loading={decide.isPending}
                  onClick={() => decide.mutate(open)}
                  data-confirm-decision={open}
                >
                  {open === 'approve' ? 'Approve and grant hosting' : 'Send decline'}
                </Button>
                <Button variant="ghost" disabled={decide.isPending} onClick={() => setOpen(null)}>
                  Cancel
                </Button>
              </>
            )}
          </div>
        </div>
      ) : (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 border-t border-line pt-4 text-xs text-faint">
          <span className="meta-caps">
            {application.status} {application.decided_at && shortDate(application.decided_at)}
          </span>
          {application.reviewer && <span>by @{application.reviewer}</span>}
          {application.decision_note && (
            <p className="w-full text-sm text-soft">“{application.decision_note}”</p>
          )}
        </div>
      )}
    </Card>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="meta-caps text-faint">{label}</dt>
      <dd className="text-ink">{value}</dd>
    </div>
  );
}

function shortDate(iso: string): string {
  return new Date(iso.replace(' ', 'T') + 'Z').toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}
